import React from 'react';
import { Play, Pause, Square, Navigation } from 'lucide-react';
import { clsx } from 'clsx';
import type { PlaybackState } from '../../types/tracking';
import { PLAYBACK_SPEEDS } from '../../utils/constants';
import { formatTime } from '../../utils/dateHelpers';
import { formatDistance } from '../../utils/dateHelpers';

interface PlaybackControlsProps {
  playbackState: PlaybackState;
  onPlay: () => void;
  onPause: () => void;
  onStop: () => void;
  onSetSpeed: (speed: number) => void;
  onSeekTo: (index: number) => void;
}

export const PlaybackControls: React.FC<PlaybackControlsProps> = ({
  playbackState,
  onPlay,
  onPause,
  onStop,
  onSetSpeed,
  onSeekTo,
}) => {
  const {
    isPlaying,
    speed,
    currentIndex,
    currentTimestamp,
    totalPoints,
    distanceCoveredKm,
    totalDistanceKm,
    startTime,
    endTime,
  } = playbackState;

  const progress = totalPoints > 1 ? (currentIndex / (totalPoints - 1)) * 100 : 0;

  if (totalPoints < 2) return null;

  return (
    <div className="bg-white border-t border-rgf-gray-200">
      {/* Timeline bar */}
      <div className="px-4 pt-2 pb-1">
        <div className="flex items-center justify-between text-xs text-rgf-gray-500 mb-1">
          <span>{startTime ? formatTime(startTime) : '--:--'}</span>
          <span className="font-medium text-rgf-gray-700">
            {currentTimestamp ? formatTime(currentTimestamp) : '--:--'}
          </span>
          <span>{endTime ? formatTime(endTime) : '--:--'}</span>
        </div>
        <input
          type="range"
          min={0}
          max={totalPoints - 1}
          value={currentIndex}
          onChange={e => onSeekTo(Number(e.target.value))}
          className="w-full h-1.5 accent-rgf-primary-600 cursor-pointer"
        />
      </div>

      {/* Controls row */}
      <div className="px-4 py-2 flex items-center gap-3">
        {/* Play / Pause */}
        <button
          onClick={isPlaying ? onPause : onPlay}
          className="p-1.5 rounded-full bg-rgf-primary-600 text-white hover:bg-rgf-primary-700 transition-colors"
        >
          {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
        </button>

        {/* Stop */}
        <button
          onClick={onStop}
          className="p-1.5 rounded-full bg-rgf-gray-200 text-rgf-gray-600 hover:bg-rgf-gray-300 transition-colors"
        >
          <Square className="h-3.5 w-3.5" />
        </button>

        {/* Speed selector */}
        <div className="flex items-center gap-1 border border-rgf-gray-200 rounded-lg overflow-hidden">
          {PLAYBACK_SPEEDS.map(s => (
            <button
              key={s}
              onClick={() => onSetSpeed(s)}
              className={clsx(
                'px-2 py-1 text-xs font-medium transition-colors',
                speed === s
                  ? 'bg-rgf-primary-600 text-white'
                  : 'text-rgf-gray-600 hover:bg-rgf-gray-100'
              )}
            >
              {s}x
            </button>
          ))}
        </div>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Distance covered */}
        <div className="flex items-center gap-1.5 text-xs text-rgf-gray-600">
          <Navigation className="h-3.5 w-3.5 text-rgf-primary-600" />
          <span className="font-medium">{formatDistance(distanceCoveredKm)}</span>
          <span className="text-rgf-gray-400">/</span>
          <span>{formatDistance(totalDistanceKm)}</span>
        </div>

        {/* Progress */}
        <span className="text-xs text-rgf-gray-400 min-w-[36px] text-right">
          {Math.round(progress)}%
        </span>
      </div>
    </div>
  );
};
